import React from 'react';
import classnames from 'classnames';
import isString from 'lodash/isString';

import styles from './SelectListItem.module.scss';

import { Icon, IconType } from '@components/Icon';
import { Tooltip } from '@components/Tooltip';
import { translate } from '@chaika/i18n/helper';
import { LabelType, MeatballButtonMenuItemWithLabel } from '@components/ActionButtons/MeatballButton/MeatballButton';
import { SelectListNew } from '@components/SelectListNew';

function resolveLabel(label: LabelType): string | React.ReactElement<any> {
    if (isString(label)) {
        // @ts-expect-error
        return translate(label);
    }
    return label as React.ReactElement<any>;
}

interface Props {
    item: MeatballButtonMenuItemWithLabel;
    maxWidth?: number;
    onClose(): void;
    onItemClick?(id: string): void | Promise<void>;
}

export function SelectListItem({ item, onClose, onItemClick, maxWidth }: Props) {
    const [isSubOpen, setIsSubOpen] = React.useState(false);
    const [isPositioned, setIsPositioned] = React.useState(false);
    const [subPos, setSubPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const itemRef = React.useRef<HTMLDivElement>(null);
    const submenuRef = React.useRef<HTMLDivElement>(null);
    const subItems = item.items ?? [];
    const hasChildren = subItems.length > 0;

    const displayLabel = item.shortLabel ? resolveLabel(item.shortLabel) : resolveLabel(item.label);

    function handleMouseEnter(): void {
        if (!hasChildren) return;

        setIsPositioned(false);
        setIsSubOpen(true);
    }

    React.useEffect(() => {
        if (!isSubOpen || !submenuRef.current || !itemRef.current) return;

        const SUBMENU_TOP_OFFSET = 8;
        const BOTTOM_GAP = 10;
        const rect = itemRef.current.getBoundingClientRect();
        const submenuHeight = submenuRef.current.offsetHeight;
        const submenuWidth = submenuRef.current.offsetWidth;

        const fitsRight = rect.right + submenuWidth <= window.innerWidth;

        const left = fitsRight ? rect.right : rect.left - submenuWidth;
        const defaultTop = rect.top - SUBMENU_TOP_OFFSET;
        const overflowBottom = defaultTop + submenuHeight - (window.innerHeight - BOTTOM_GAP);
        const top = Math.max(0, defaultTop - Math.max(0, overflowBottom));

        setSubPos({ top, left });

        setIsPositioned(true);
    }, [isSubOpen]);

    return (
        <div
            ref={itemRef}
            data-testid={item.dataTest}
            className={classnames([
                styles.root,
                hasChildren && styles.withArrow,
                item.disable && styles.item_disabled,
                item.isHighlighted && styles.item_highlighted,
            ])}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={hasChildren ? () => setIsSubOpen(false) : undefined}
            onClick={
                item.disable || hasChildren
                    ? undefined
                    : () => (item.onClick ? item.onClick(item.value) : onItemClick?.(item.value ?? ''))
            }
        >
            <Tooltip
                title={item.disableHint ?? ''}
                disable={!item.disableHint}
            >
                <div className={styles.itemLabel}>
                    {displayLabel}
                    {hasChildren && (
                        <span className={styles.itemArrow}>
                            <Icon type={IconType.ARROW_LEFT} />
                        </span>
                    )}
                </div>
            </Tooltip>
            {!item.disable && hasChildren && isSubOpen && (
                <div
                    ref={submenuRef}
                    className={styles.submenu}
                    style={{
                        top: subPos.top,
                        left: subPos.left,
                        visibility: isPositioned ? 'visible' : 'hidden',
                    }}
                    onMouseEnter={() => setIsSubOpen(true)}
                    onMouseLeave={() => setIsSubOpen(false)}
                >
                    <SelectListNew
                        maxWidth={maxWidth}
                        hasSelected={item.items?.some(item => !('divider' in item) && item.isHighlighted)}
                        onItemClick={onItemClick}
                        items={subItems}
                        onClose={onClose}
                    />
                </div>
            )}
        </div>
    );
}
